import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { TriCheckbox } from "@nova/component-checkbox"
import { WorkspaceCompactTextInput } from "@nova/component-inline-text-input"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import classNames from "classnames"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectEntityById } from "panels/content/side-panel/slice"
import {
    SidePanelArchivedLayersData,
    useArchivedLayersCategories,
    useFilteredArchivedLayersData,
} from "panels/content/side-panel/useSidePanelArchivedLayers"
import { useCallback, useMemo, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useDebounceDispatch } from "tools/store"
import {
    addArchivedLayersToSelection,
    removeArchivedLayersFromSelection,
    selectIsUnarchiving,
    selectMultipleLayers,
    selectSearchArchivedLayersValue,
    selectSelectedArchivedLayerIds,
    setIsUnarchiving,
    setSearchArchivedLayersValue,
} from "panels/content/side-panel/slice.new"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { DateFormat, formatDate } from "@nova/format"
import {
    selectActiveWorkspaceId,
    selectWorkspaceRegion,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectActivatedFireId } from "features/map/control/selectors"
import { concurrentUnarchiveCount, SidePanelLayerType } from "@nova/constants"
import store from "integrations/redux/store"
import { RootState } from "integrations/redux"
import { ScrollableArea } from "@nova/component-scrollable-area"
import {
    createFractionalIndexBetween,
    createNewLayerFractionalIndex,
    END,
} from "@nova/serializer/src/client/util/fractional_indexing"
import { selectProject } from "features/entity-caching/project/module"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { selectUserID } from "user/profile/selectors"
import { activateOrthophoto } from "plugins/Orthomosaic/OrthomosaicSlice"
import { Semaphore } from "async-mutex"
import {
    getSidePanelLayerTypeToPluginNameMap,
    getSidePanelLayerTypeToPrimaryIconMap,
} from "plugins/plugin-registry"
import { hideGlobalModal } from "runlevel/actions"

const unarchiveSemaphore = new Semaphore(concurrentUnarchiveCount)

export const UnarchiveLayersModal = () => {
    const dispatch = useAppDispatch()
    const selectedArchivedLayerIds = useAppSelector(selectSelectedArchivedLayerIds)
    const isUnarchiving = useAppSelector(selectIsUnarchiving)
    const confirmButtonText = useMemo(() => {
        switch (selectedArchivedLayerIds.length) {
            case 0:
                return "Retrieve Layers"
            case 1:
                return "Retrieve 1 Layer"
            default:
                return `Retrieve ${selectedArchivedLayerIds.length} Layers`
        }
    }, [selectedArchivedLayerIds.length])
    const orgId = useAppSelector(selectActiveWorkspaceId()) || ""
    const projectId = useAppSelector(selectActivatedFireId) || ""
    const userId = useAppSelector(selectUserID) || ""
    const region = useAppSelector(selectWorkspaceRegion()) || ""
    const cleanupAndClose = useCallback(() => {
        dispatch(removeArchivedLayersFromSelection({ ids: selectedArchivedLayerIds }))
        dispatch(setSearchArchivedLayersValue(""))
        dispatch(hideGlobalModal())
    }, [dispatch, selectedArchivedLayerIds])
    const loadingText = useLoadingText(isUnarchiving, "Retrieving Archived Layers")
    return (
        <HighPriorityNovaModalContainer
            className="w-2/3 max-w-[1100px] !min-w-[675px] max-h-[90%]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary
                        title="Cancel"
                        onClick={cleanupAndClose}
                        disabled={isUnarchiving}
                    />
                    <ButtonPrimary
                        title={confirmButtonText}
                        RightIcon={Icons.ArrowRight}
                        onClick={async () => {
                            if (!selectedArchivedLayerIds.length) return
                            dispatch(setIsUnarchiving(true))
                            const state = store.getState() as RootState
                            const indexes = new Array(selectedArchivedLayerIds.length)
                            const project = selectProject(state)
                            indexes[0] = createNewLayerFractionalIndex(project)
                            for (let i = 1; i < selectedArchivedLayerIds.length; i++) {
                                indexes[i] = createFractionalIndexBetween(indexes[i - 1], END)
                            }
                            let errorCount = 0
                            await Promise.all(
                                selectedArchivedLayerIds.map((id, i) => {
                                    const entity = selectEntityById(id)(state)
                                    if (!entity?.type) return
                                    const task = async () => {
                                        const [, release] = await unarchiveSemaphore.acquire()
                                        try {
                                            const res = await fetch(
                                                `/api/protobuf/org/${orgId}/project/${projectId}/user/${userId}/${getSidePanelLayerTypeToPluginNameMap()[entity.type!]}/${id}/unarchive/newIndex/${indexes[i]}`,
                                                {
                                                    method: "POST",
                                                    headers: {
                                                        region,
                                                    },
                                                }
                                            )
                                            if (!res.ok) {
                                                errorCount += 1
                                            } else if (
                                                entity.type === SidePanelLayerType.OrthomosaicFlight
                                            ) {
                                                dispatch(activateOrthophoto(id))
                                            }
                                        } catch {
                                            errorCount += 1
                                        } finally {
                                            release()
                                        }
                                    }
                                    return task()
                                })
                            )
                            if (errorCount > 0) {
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: `${errorCount > 1 ? errorCount + " layers" : "1 layer"} failed to unarchive.`,
                                        status: GlobalNotificationStatus.error,
                                        timeoutInMs: 3000,
                                    })
                                )
                            }
                            dispatch(selectMultipleLayers({ ids: selectedArchivedLayerIds }))
                            dispatch(setIsUnarchiving(false))
                            cleanupAndClose()
                        }}
                        disabled={selectedArchivedLayerIds.length === 0 || isUnarchiving}
                        isLoading={isUnarchiving}
                    />
                </div>
            }
            title={isUnarchiving ? loadingText : "Retrieve Archived Layers"}
            closeModal={cleanupAndClose}
            canCloseModal={!isUnarchiving}
        >
            <div className="flex gap-6 shrink min-h-0">
                <CategoryList />
                <LayersList />
            </div>
        </HighPriorityNovaModalContainer>
    )
}

function CategoryList() {
    const archivedLayerCategories = useArchivedLayersCategories()
    return (
        <ScrollableArea className="w-[250px]">
            <div className="flex flex-col gap-0.5 w-full">
                {Object.entries(archivedLayerCategories).map(([categoryName, ids]) => {
                    return (
                        <CategoryListItem
                            key={categoryName}
                            categoryName={categoryName}
                            entityIds={ids}
                        />
                    )
                })}
            </div>
        </ScrollableArea>
    )
}

function CategoryListItem({
    categoryName,
    entityIds,
}: {
    categoryName: string
    entityIds: string[]
}) {
    const dispatch = useAppDispatch()
    const isUnarchiving = useAppSelector(selectIsUnarchiving)
    const selectedArchivedLayerIds = useAppSelector(selectSelectedArchivedLayerIds)
    const allEntitiesAreSelected = useMemo(
        () => entityIds.every((id) => selectedArchivedLayerIds.includes(id)),
        [entityIds, selectedArchivedLayerIds]
    )
    const someEntityIsSelected = useMemo(
        () => entityIds.some((id) => selectedArchivedLayerIds.includes(id)),
        [entityIds, selectedArchivedLayerIds]
    )
    const handleSelect = useCallback(() => {
        if (allEntitiesAreSelected) {
            dispatch(removeArchivedLayersFromSelection({ ids: entityIds }))
        } else {
            dispatch(addArchivedLayersToSelection({ ids: entityIds }))
        }
    }, [allEntitiesAreSelected, dispatch, entityIds])
    return (
        <div
            className={classNames("flex gap-3 items-center px-2 py-[6px] rounded-lg w-full", {
                "bg-white-200": someEntityIsSelected,
                "cursor-pointer hover:bg-white-200": !isUnarchiving,
            })}
            onClick={isUnarchiving ? undefined : handleSelect}
        >
            <TriCheckbox
                size="small"
                state={
                    allEntitiesAreSelected
                        ? "checked"
                        : someEntityIsSelected
                          ? "indeterminate"
                          : "unchecked"
                }
                onClick={handleSelect}
                isDisabled={isUnarchiving}
            />
            <small className="text-white-700 select-none">{categoryName}</small>
            <div className="grow min-w-0" />
            <small className="text-nova-gray select-none">{entityIds.length}</small>
        </div>
    )
}

function LayersList() {
    const dispatch = useAppDispatch()
    const { debounceDispatch } = useDebounceDispatch(500)
    const isUnarchiving = useAppSelector(selectIsUnarchiving)
    const archivedLayerData = useFilteredArchivedLayersData()
    const selectedArchivedLayerIds = useAppSelector(selectSelectedArchivedLayerIds)
    const everythingIsSelected = useMemo(
        () => selectedArchivedLayerIds.length === archivedLayerData.length,
        [archivedLayerData.length, selectedArchivedLayerIds.length]
    )
    const searchArchivedLayersValue = useAppSelector(selectSearchArchivedLayersValue)
    const [localSearchValue, setLocalSearchValue] = useState(searchArchivedLayersValue)
    return (
        <div className="flex flex-col w-full gap-2 shrink min-h-0">
            <div>
                <WorkspaceCompactTextInput
                    name="archivedLayersSearch"
                    placeholder="Search Layers Name"
                    className="w-full"
                    inputClassName="!placeholder-gray-300 hover:!placeholder-gray-100 transition-colors"
                    isClearable
                    leftIcon={Icons.MagnifyingGlass}
                    value={localSearchValue}
                    handleChange={(text) => {
                        setLocalSearchValue(text)
                        debounceDispatch(setSearchArchivedLayersValue(text))
                    }}
                    disabled={isUnarchiving}
                />
            </div>
            <div className="w-full flex flex-col rounded-lg overflow-hidden shrink min-h-0">
                <div className="flex gap-3 items-center py-[10px] px-3 bg-white-100 relative">
                    <TriCheckbox
                        size="small"
                        state={
                            !selectedArchivedLayerIds.length
                                ? "unchecked"
                                : selectedArchivedLayerIds.length === archivedLayerData.length
                                  ? "checked"
                                  : "indeterminate"
                        }
                        onClick={() => {
                            if (everythingIsSelected) {
                                dispatch(
                                    removeArchivedLayersFromSelection({
                                        ids: archivedLayerData.map((layer) => layer.id),
                                    })
                                )
                            } else {
                                dispatch(
                                    addArchivedLayersToSelection({
                                        ids: archivedLayerData.map((layer) => layer.id),
                                    })
                                )
                            }
                        }}
                        isDisabled={isUnarchiving}
                    />
                    <small className="xs text-gray-300 select-none">Layer Name</small>
                    <div className="grow" />
                    <small className="xs text-gray-300 select-none">Date Archived</small>
                </div>
                <ScrollableArea shouldPreventScrollbarShifting className="h-full">
                    {archivedLayerData.map((layer) => (
                        <LayersListItem
                            key={layer.id}
                            layerData={layer}
                            isSelected={selectedArchivedLayerIds.includes(layer.id)}
                        />
                    ))}
                </ScrollableArea>
            </div>
        </div>
    )
}

function LayersListItem({
    layerData,
    isSelected,
}: {
    layerData: SidePanelArchivedLayersData
    isSelected: boolean
}) {
    const dispatch = useAppDispatch()
    const isUnarchiving = useAppSelector(selectIsUnarchiving)
    const entity = useAppSelector(selectEntityById(layerData.id))
    const handleSelect = useCallback(() => {
        if (isSelected) {
            dispatch(removeArchivedLayersFromSelection({ ids: [layerData.id] }))
        } else {
            dispatch(addArchivedLayersToSelection({ ids: [layerData.id] }))
        }
    }, [dispatch, isSelected, layerData.id])
    return (
        <div
            className={classNames(
                "flex items-center gap-3 py-[10px] px-3 relative",
                "border-t border-white-200",
                {
                    "hover:bg-white-100": !isSelected && !isUnarchiving,
                    "bg-white-200": isSelected,
                    "cursor-pointer": !isUnarchiving,
                }
            )}
            onClick={isUnarchiving ? undefined : handleSelect}
        >
            <TriCheckbox
                size="small"
                state={isSelected ? "checked" : "unchecked"}
                onClick={handleSelect}
                isDisabled={isUnarchiving}
            />
            {!!entity.type ? (
                <Icons.Icon
                    Icon={getSidePanelLayerTypeToPrimaryIconMap()[entity.type]}
                    iconClassName="text-white-400"
                />
            ) : (
                <div className="w-6" />
            )}
            <h5 className="select-none">{entity.name}</h5>
            <div className="grow" />
            <small
                className={classNames("select-none", {
                    "text-gray-300": !isSelected,
                    "text-gray-100": isSelected,
                })}
            >
                {formatDate({
                    value: dateFromNumber(entity.archivedTimestamp),
                    format: DateFormat.MMMDDYYYY,
                    omitSuffix: true,
                })}
            </small>
        </div>
    )
}
